import { useMemo, useState, ChangeEvent } from 'react'
import { Tree, Skeleton, Button, Input, message, Space } from "antd"
import intersection from 'lodash/intersection'
import accentFold from 'utils/accentFold'
import highlightText from 'utils/highlightText'
import User from 'graphql/User/User';
import copyToClipboard from 'copy-to-clipboard'
import { DataNode } from 'rc-tree/lib/interface';
import { CheckSquareOutlined, CloseSquareOutlined, FilterOutlined } from '@ant-design/icons'
import { prettifyPhoneString } from 'pages/Chats/ChatOnlinePage/util/phoneNumberConversion'

const { Search } = Input;

export interface TreeDataNode {
  key: string,
  title: string,
  children?: TreeDataNode[],
}

export function sortTreeDataByTitle(treeDataNodes: TreeDataNode[]) {
  const treeDataNodesCopy = [...treeDataNodes]
  treeDataNodesCopy.sort((a, b) => a.title.localeCompare(b.title))
  treeDataNodesCopy.forEach(node => {
    if (node.children) node.children = sortTreeDataByTitle(node.children)
  })
  return treeDataNodesCopy
}

export function userToTreeDataNodes(user: User): TreeDataNode[] {
  if (!user || !user.agent || !user.agent.agencies) return []

  const agencyParentsInUser = user.getAgencyParents()
  const agencyParents: TreeDataNode[] = agencyParentsInUser.map<TreeDataNode>(a => ({ key: a.id, title: a.name, children: [] }))

  for (const agencyChild of user.agent.agencies) {
    const agencyParent = agencyParents.find(a => a.key === agencyChild.parent.id)
    agencyParent.children.push({
      key: agencyChild.id,
      title: `${agencyChild.name} (${agencyChild.chatWidgets.length})`,
      children: agencyChild.chatWidgets.map(c => ({
        key: c.id,
        title: `${c.name} (${c.externalChatChannels.length})`,
        children: c.externalChatChannels.map(e => ({
          key: e.id,
          title: `${e.name} - ${prettifyPhoneString(e.idInChannel)}`
        }))
      }))
    })
  }
  // Add children count
  agencyParents.forEach(a => a.title = a.title + ` (${a.children.length})`)

  // Sort everything by name
  return sortTreeDataByTitle(agencyParents)
}

const getDataNodes = (args: {
  treeDataNodes: TreeDataNode[]
  searchValue?: string
  idsToHide?: string[]
  hideEmptyParentNodes?: boolean
}): { treeDataNodes: DataNode[], expandedKeys: string[] } => {
  const { treeDataNodes, searchValue, idsToHide = [], hideEmptyParentNodes = false } = args
  const hiddenIds = new Set(idsToHide)
  const normalizedSearch = searchValue ? accentFold(searchValue.toLowerCase()) : null
  const expandedKeys = new Set<string>()

  const buildNodes = (
    nodes: TreeDataNode[],
    ancestors: string[] = [],
    ancestorMatchesSearch = false,
    isRoot = false
  ): DataNode[] => nodes.reduce<DataNode[]>((result, node) => {
    if (hiddenIds.has(node.key)) return result

    const matchesSearch = normalizedSearch !== null &&
      accentFold(node.title.toLowerCase()).includes(normalizedSearch)
    const children = node.children
      ? buildNodes(node.children, [...ancestors, node.key], ancestorMatchesSearch || matchesSearch)
      : undefined
    const isVisible = normalizedSearch === null || ancestorMatchesSearch || matchesSearch || Boolean(children?.length)

    if (!isVisible || (isRoot && hideEmptyParentNodes && !children?.length)) return result
    if (matchesSearch) ancestors.forEach(key => expandedKeys.add(key))

    result.push({
      key: node.key,
      title: highlightText(node.title, searchValue),
      children,
    })
    return result
  }, [])

  return {
    treeDataNodes: buildNodes(treeDataNodes, [], false, true),
    expandedKeys: Array.from(expandedKeys),
  }
}

const getAllKeys = (dataNodes: DataNode[]): string[] =>
  dataNodes.reduce<string[]>((result, node) => {
    result.push(node.key as string)
    if (node.children) result.push(...getAllKeys(node.children))
    return result
  }, [])


export interface Props {
  user: User
  onWidgetIdsCheckedChange?: (selectedWidgetIds: string[]) => void
  onWidgetIdSelected?: (selectedWidgetId: string | null) => void
  onAgencyIdsCheckedChange?: (selectedAgencyIds: string[]) => void
  onAgencyIdSelected?: (selectedAgencyId: string | null) => void
  checkedWidgetIds?: string[]
  hideCheckboxes?: boolean
  enableCopyToClipboard?: boolean
  idsToHide?: string[]
  /** hide agency parents that have no child nodes */
  hideEmptyParentNodes?: boolean
}

const WidgetIdsSelectionTree = (props: Props) => {
  const {
    user,
    checkedWidgetIds = [],
    hideCheckboxes = false,
    enableCopyToClipboard = true,
    onWidgetIdsCheckedChange,
    onWidgetIdSelected,
    onAgencyIdsCheckedChange,
    onAgencyIdSelected,
    idsToHide,
    hideEmptyParentNodes = false,
  } = props

  const allIds = useMemo(() => user ? user.getAllAgencyAndWidgetIds() : [], [user])
  const allWidgetIds = useMemo(() => user ? user.getAllWidgetIds() : [], [user])
  const allAgencyIds = useMemo(() => user ? user.getAllAgencyIds() : [], [user])

  const [searchValue, setSearchValue] = useState<string>(null)
  const [showSearch, setShowSearch] = useState<boolean>(hideCheckboxes);

  const [checkedKeys, setCheckedKeys] = useState<string[]>(checkedWidgetIds)
  // const allChecked = allIds.length === checkedKeys.length
  const someChecked = checkedKeys.length > 0
  const lotsOfAgencies = user && user.agent && user.agent.agencies && user.agent.agencies.length > 1;

  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  const treeDataNodes = useMemo(() => userToTreeDataNodes(user), [user])
  const { treeDataNodes: treeNodesRendered } = useMemo(
    () => getDataNodes({ treeDataNodes, searchValue, idsToHide, hideEmptyParentNodes }),
    [treeDataNodes, searchValue, idsToHide, hideEmptyParentNodes]
  )
  const renderedKeys = useMemo(() => new Set(getAllKeys(treeNodesRendered)), [treeNodesRendered])


  const handleCheck = (checkedKeysNew: string[]) => {
    setCheckedKeys(checkedKeysNew)
    // console.log({ checkedKeysNew })
    const widgetIds = checkedKeysNew.filter(c => allWidgetIds.includes(c))
    onWidgetIdsCheckedChange && onWidgetIdsCheckedChange(widgetIds)

    const agencyIds = checkedKeysNew.filter(c => allAgencyIds.includes(c))
    onAgencyIdsCheckedChange && onAgencyIdsCheckedChange(agencyIds)
  }

  /** the Tree only reports keys of rendered nodes, so keep the checked keys that are currently filtered out */
  const handleTreeCheck = (treeCheckedKeys: string[]) => {
    const checkedKeysNotRendered = checkedKeys.filter(key => !renderedKeys.has(key))
    handleCheck([...checkedKeysNotRendered, ...treeCheckedKeys])
  }

  const handleSelect = (selectedKeys: string[]) => {
    if (onWidgetIdSelected) {
      const selectedWidgetIds = intersection(allWidgetIds, selectedKeys)
      const selectedWidgetId = selectedWidgetIds[0] || null
      onWidgetIdSelected(selectedWidgetId)
    }

    if (onAgencyIdSelected) {
      const selectedAgencyIds = intersection(allAgencyIds, selectedKeys)
      const selectedAgencyId = selectedAgencyIds[0] || null
      onAgencyIdSelected(selectedAgencyId)
    }

    const selectedKey = selectedKeys[0] || null
    if (enableCopyToClipboard && selectedKey) {
      copyToClipboard(selectedKey)
      message.info(`id '${selectedKey}' copiado para a área de transferência!`)
    }
  }


  const checkAll = (val: boolean) => {
    //console.log("changeAll("+val+")");
    const keys = val ? allIds : []
    handleCheck(keys)
  }

  const handleSearchFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.length > 2 ?
      e.target.value :
      null
    setSearchValue(value)
    const { expandedKeys: expandedKeysNew } = getDataNodes({
      treeDataNodes,
      searchValue: value,
      idsToHide,
      hideEmptyParentNodes,
    })
    setExpandedKeys(expandedKeysNew)
  };

  if (!(user && user.agent && user.agent.agencies && user.agent.agencies))
    return <div>Usuário não possui vinculos ativos</div>

  return <>
    {user ? <>
      {lotsOfAgencies &&
        <>
          {!hideCheckboxes &&
            <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
              <Button
                onClick={() => checkAll(!someChecked)}
                block
                icon={someChecked ? <CloseSquareOutlined /> : <CheckSquareOutlined />}
              >
                {(someChecked ? "Desmarcar" : "Selecionar") + " todos"}
              </Button>
              <Button
                onClick={() => setShowSearch(!showSearch)}
                icon={<FilterOutlined />}
                type={(showSearch || searchValue) ? 'primary' : 'default'}
              />
            </Space.Compact>
          }
          {(showSearch || searchValue) &&
            <Search
              style={{ marginBottom: 8, marginTop: 8 }}
              placeholder="Buscar"
              onChange={handleSearchFieldChange}
              allowClear
              // with checkboxes the search is only mounted by clicking the filter button, so focus it
              autoFocus={!hideCheckboxes}
            />
          }
        </>
      }

      <Tree
        checkable={!hideCheckboxes}
        checkedKeys={checkedKeys}
        onCheck={checkedKeys => handleTreeCheck(checkedKeys as string[])}
        expandedKeys={expandedKeys}
        onExpand={(e) => setExpandedKeys(e as string[])}
        onSelect={selectedKeys => handleSelect(selectedKeys as string[])}
        treeData={treeNodesRendered}
      />
    </>
      :
      <Skeleton active />
    }
  </>;
}

export default WidgetIdsSelectionTree;
